import type { ReviewedRow } from './types'

export type ReviewDecision = 'approved' | 'rejected'

export const decisionChoices = [
  { value: 'approved' as const, label: 'Approve' },
  { value: 'rejected' as const, label: 'Reject' },
]

/** A row with no catalog product is added as a custom line for Sales to price (USA-1338). */
export const customDecisionChoices = [
  { value: 'approved' as const, label: 'Add as custom line' },
  { value: 'rejected' as const, label: 'Reject' },
]

// Approving a child keeps its parent; rejecting a parent drops its children.
export function decideRow(
  current: Record<string, ReviewDecision>,
  row: Pick<ReviewedRow, 'row_id' | 'parent_row_id'>,
  rows: Array<Pick<ReviewedRow, 'row_id' | 'parent_row_id'>>,
  decision: ReviewDecision,
) {
  const next = { ...current, [row.row_id]: decision }
  if (decision === 'approved') {
    let parentId = row.parent_row_id
    while (parentId) {
      next[parentId] = 'approved'
      parentId = rows.find((item) => item.row_id === parentId)?.parent_row_id ?? null
    }
  } else {
    const rejected = new Set([row.row_id])
    for (const item of rows) {
      if (item.parent_row_id && rejected.has(item.parent_row_id)) {
        rejected.add(item.row_id)
        next[item.row_id] = 'rejected'
      }
    }
  }
  return next
}

type Candidate = Pick<ReviewedRow['candidates'][number], 'match_type'>

/** The match a row starts with. A close match waits for the reviewer to choose it (USA-1339). */
export function defaultCandidate<T extends Candidate>(candidates: T[], exactOnly = false) {
  return candidates.find((candidate) =>
    exactOnly ? candidate.match_type === 'exact' : candidate.match_type !== 'close_match',
  )
}

const keyCharacter = /[a-z0-9]/i

/**
 * The catalog model split around what differs from the printed one, ignoring case and punctuation:
 * printed TRCB-48 and catalog TRCB-48-HC give `['TRCB-48', '-HC', '']`.
 */
export function modelDifference(printed: string, catalog: string): [string, string, string] {
  const key = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')
  const printedKey = key(printed)
  if (!printedKey) return [catalog, '', '']
  const positions = [...catalog].flatMap((character, index) => (keyCharacter.test(character) ? [index] : []))
  const catalogKey = key(catalog)
  let prefix = 0
  while (prefix < printedKey.length && prefix < catalogKey.length && printedKey[prefix] === catalogKey[prefix]) prefix++
  let suffix = 0
  while (
    suffix < printedKey.length - prefix &&
    suffix < catalogKey.length - prefix &&
    printedKey[printedKey.length - 1 - suffix] === catalogKey[catalogKey.length - 1 - suffix]
  )
    suffix++
  if (prefix + suffix >= catalogKey.length) return [catalog, '', '']
  const start = prefix ? positions[prefix - 1]! + 1 : 0
  const end = suffix ? positions[catalogKey.length - suffix]! : catalog.length
  return [catalog.slice(0, start), catalog.slice(start, end), catalog.slice(end)]
}

// The server counts rows without an exact or AI match once, as the import is read
// (`reviewed_extraction`); here the count follows what the reviewer has chosen since.
const manualResolution = /^\d+ row\(s\) require manual resolution$/

/** The import's warnings, with rows still needing a product counted as they are now. */
export function documentWarnings(
  warnings: string[],
  rows: Array<Pick<ReviewedRow, 'row_id' | 'unresolved'>>,
  matches: Record<string, number>,
) {
  const unresolved = rows.filter((row) => row.unresolved && !matches[row.row_id]).length
  return [
    ...warnings.filter((warning) => !manualResolution.test(warning)),
    ...(unresolved ? [`${unresolved} row(s) require manual resolution`] : []),
  ]
}
